import { NormalizeOAS, OASRequestParams, OASResponse } from 'fets';
import type assetLibraryOpenApi from '../asset-library-open-api';
import { AssetLibraryFetsApiClient } from '../asset-library-fets-client';
import { assertNever } from '../utils';
import { Guid } from '../../api/public/utils/Guid';

const PATH = '/api/v2/asset/company/{companyId}/query';

export type AssetSortField = 'UploadedAt' | 'Name' | 'Size';
export type SortDirection = 'Asc' | 'Desc';
export type AssetProcessingState = 'Ready' | 'Processing' | 'Failed';
export type AssetType = 'Image' | 'Audio' | 'Video' | 'Document' | 'Subtitle' | 'Web';

type Params = {
  companyId: Guid;
  skip: number;
  limit: number;
  sortField: AssetSortField;
  sortDirection: SortDirection;
  directoryId?: Guid | null;
  search?: string | null;
  types?: AssetType[] | null;
  states?: AssetProcessingState[] | null;
};

export type AssetListItem = {
  id: Guid;
  name: string;
  size: number;
  /** Unix timestamp in seconds. */
  uploadedAt: number;
  state: AssetProcessingState;
  /** The folder holding the asset. The root directory's guid for an asset at the top level. */
  directoryId: Guid;
  thumbnailUrl: string | null;
};

type Result = {
  total: number;
  items: AssetListItem[];
};

export type ApiFunction = (params: Params, signal?: AbortSignal) => Promise<Result>;

export const createQueryAssets = (apiClient: AssetLibraryFetsApiClient): ApiFunction => {
  const queryAssets = async (params: Params, signal?: AbortSignal): Promise<Result> => {
    const response = await apiClient[PATH].post({
      ...convertToRequest(params),
      ...(signal && { signal }),
    });

    return convertResponse(response);
  };

  return queryAssets;
};

type Request = OASRequestParams<NormalizeOAS<typeof assetLibraryOpenApi>, typeof PATH, 'post'>;
type Response = OASResponse<NormalizeOAS<typeof assetLibraryOpenApi>, typeof PATH, 'post'>;

const convertToRequest = (params: Params): Request => ({
  params: { companyId: params.companyId },
  json: {
    skip: params.skip,
    limit: params.limit,
    sortField: params.sortField,
    sortDirection: params.sortDirection,
    directoryId: params.directoryId ?? null,
    search: params.search ?? null,
    types: params.types ?? null,
    states: params.states ?? null,
  },
});

const convertResponse = async (response: Response): Promise<Result> => {
  const responseStatus = response.status;
  if (responseStatus !== 200) {
    return assertNever(responseStatus);
  }

  const data = await response.json();
  const result: Result = {
    total: data.total,
    items: data.items.map((item) => ({
      id: item.id,
      name: item.name,
      size: item.size,
      uploadedAt: Number(item.uploadedAt),
      state: item.state,
      directoryId: item.directoryId,
      thumbnailUrl: item.thumbnailUrl ?? null,
    })),
  };
  return result;
};
